import type {
  Availability,
  AvatarRelease,
  Employee,
  InterviewSession,
  KnowledgeSource,
  Release,
  Revision,
  TenantInfo,
  ToolEvent,
  Turn,
} from "../ports.ts";
import type { ContextDraft } from "../domain/draft.ts";
import type { PipelineState, Stage, StageStatus } from "../domain/stages.ts";
import type { Job } from "../jobs/queue.ts";
import type { EmployeeReleaseBundle } from "@delegate/employee-contract";

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export type BuilderConfig = {
  studioUrl: string;
  stages: Stage[];
  coverageTopics: { id: string; label: string; prompt: string }[];
  realtimeModel: string;
  limits: { interviewMaxPerHour: number; interviewMaxMinutes: number; searchMaxPerSession: number; uploadMaxBytes: number };
  canAssignAll: boolean;
};

export type EmployeeSummary = Employee & { stages: Record<Stage, StageStatus>; latestVersion: string | null };

export type EmployeeDetail = {
  employee: Employee;
  state: PipelineState & { avatarLiveKnown: boolean };
  stages: Record<Stage, StageStatus>;
  sources: KnowledgeSource[];
  sessions: InterviewSession[];
  draft: Revision | null;
  releases: Release[];
  availability: Availability | null;
  usage: { events: number; costUsd: number; byKind: Record<string, number> };
  jobs: Job[];
};

export type InterviewStart = {
  session: InterviewSession;
  clientSecret: string;
  expiresAt: string;
  model: string;
  instructions: string;
  manifestUrl: string;
  studioUrl: string;
  maxMinutes: number;
};

/**
 * Typed client for the builder router. `baseUrl` is wherever the host mounted
 * it (the operator app uses /api/builder), so the same screens work in any host.
 */
export function createBuilderApi(baseUrl: string, opts: { onUnauthorized?: () => void } = {}) {
  async function call<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
    const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
    let body = init.body;
    if (init.json !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(init.json);
    }
    const res = await fetch(`${baseUrl}${path}`, { ...init, headers, body, credentials: "same-origin" });
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    if (res.status === 401) opts.onUnauthorized?.();
    if (!res.ok) throw new ApiError(res.status, data.error ?? `HTTP ${res.status}`);
    return data as T;
  }

  return {
    config: () => call<BuilderConfig>("/config"),
    avatars: () => call<{ avatars: AvatarRelease[] }>("/avatars").then((r) => r.avatars),
    tenants: () => call<{ tenants: TenantInfo[] }>("/tenants").then((r) => r.tenants),
    tools: () => call<{ tools: { id: string; summary: string }[]; registryVersion: string }>("/tools"),

    employees: () => call<{ employees: EmployeeSummary[] }>("/employees").then((r) => r.employees),
    createEmployee: (b: { avatarReleaseId?: string; name?: string; jobTitle?: string }) => call<{ employee: Employee }>("/employees", { method: "POST", json: b }).then((r) => r.employee),
    employee: (id: string) => call<EmployeeDetail>(`/employees/${id}`),
    updateEmployee: (id: string, b: { name?: string; jobTitle?: string }) => call<{ employee: Employee }>(`/employees/${id}`, { method: "PATCH", json: b }),
    pinAvatar: (id: string, releaseId: string) => call<{ employee: Employee }>(`/employees/${id}/avatar`, { method: "PUT", json: { releaseId } }),

    uploadSource: (id: string, file: File) => {
      const form = new FormData();
      form.append("file", file);
      form.append("title", file.name);
      return call<{ source: KnowledgeSource }>(`/employees/${id}/sources`, { method: "POST", body: form });
    },
    addLink: (id: string, b: { kind: "link" | "video"; url: string; title?: string }) => call<{ source: KnowledgeSource }>(`/employees/${id}/sources`, { method: "POST", json: b }),
    retrySource: (id: string, sid: string) => call(`/employees/${id}/sources/${sid}/retry`, { method: "POST" }),
    deleteSource: (id: string, sid: string) => call(`/employees/${id}/sources/${sid}`, { method: "DELETE" }),

    startInterview: (id: string) => call<InterviewStart>(`/employees/${id}/interviews`, { method: "POST" }),
    interview: (sid: string) => call<{ session: InterviewSession; turns: Turn[]; toolEvents: ToolEvent[] }>(`/interviews/${sid}`),
    postTurn: (sid: string, role: "user" | "avatar", text: string) => call(`/interviews/${sid}/turns`, { method: "POST", json: { role, text } }),
    runTool: (sid: string, name: string, args: unknown) => call<{ output: string; ok: boolean }>(`/interviews/${sid}/tools/${name}`, { method: "POST", json: { args } }),
    endInterview: (sid: string) => call<{ session: InterviewSession }>(`/interviews/${sid}/end`, { method: "POST" }),

    distill: (id: string) => call<{ jobId: string }>(`/employees/${id}/distill`, { method: "POST" }),
    draft: (id: string) => call<{ revision: Revision & { payload: { draft: ContextDraft } }; reviewed: boolean; stale: boolean }>(`/employees/${id}/draft`),
    saveDraft: (id: string, parentId: string, draft: ContextDraft) => call<{ revision: Revision }>(`/employees/${id}/draft`, { method: "PUT", json: { parentId, draft } }),
    review: (id: string, revisionId: string) => call<{ revision: Revision }>(`/employees/${id}/draft/review`, { method: "POST", json: { revisionId } }),

    publish: (id: string, bump?: "major" | "minor" | "patch") => call<{ release: Release }>(`/employees/${id}/releases`, { method: "POST", json: bump ? { bump } : {} }),
    bundle: (rid: string) => call<EmployeeReleaseBundle>(`/releases/${rid}/bundle`),

    availability: (id: string) => call<{ versions: Availability[] }>(`/employees/${id}/availability`).then((r) => r.versions),
    setAvailability: (id: string, b: { mode: "all" | "tenants"; tenantSlugs: string[]; reason: string }) => call<{ availability: Availability }>(`/employees/${id}/availability`, { method: "PUT", json: b }),

    retryJob: (id: string, jid: string) => call(`/employees/${id}/jobs/${jid}/retry`, { method: "POST" }),
    audit: (id: string) => call<{ records: { actor: string; action: string; detail: any; at: string }[] }>(`/employees/${id}/audit`).then((r) => r.records),
  };
}

export type BuilderApi = ReturnType<typeof createBuilderApi>;
